import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  API_URL: string = environment.url;
  API_DOMAIN: string = environment.domain;

  private showNotificationsSubject = new BehaviorSubject<boolean>(false);
  showNotifications$ = this.showNotificationsSubject.asObservable();

  private profileUpdatedSource = new Subject<void>();
  profileUpdated$ = this.profileUpdatedSource.asObservable();

  notifyProfileUpdated() {
    this.profileUpdatedSource.next();
  }

  toggleNotifications(value?: any) {
    const toggle = value ? !value : !this.showNotificationsSubject.value;
    this.showNotificationsSubject.next(toggle);
  }

  setShowNotifications(state: boolean) {
    this.showNotificationsSubject.next(state);
  }

  constructor(private http: HttpClient) {}
  isLoggedIn(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  clearAuthToken() {
    localStorage.removeItem('authToken');
  }
  logoutPatient() {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.API_URL}/api/user/logout`, { headers });
  }
  loginUser(userData: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post(
      `${this.API_URL}/api/auth/login`,
      JSON.stringify(userData),
      { headers },
    );
  }

  changeStatus(data: {}) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.API_URL}/api/change-status`, data, {
      headers,
    });
  }

  getAllConsultList(payload?: any): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    // Convert payload to query params
    let params = new HttpParams();
    if (payload) {
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== null && payload[key] !== undefined) {
          params = params.set(key, payload[key]);
        }
      });
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.API_URL}/api/consultation-list`, {
      headers,
      params,
    });
  }

  getMedicalRecords(bookingId: any, userId: any) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(
      `${this.API_URL}/api/medical-record/telemedicine-medical-records/${bookingId}/${userId}`,
      { headers },
    );
  }

  getConsultationRow(id: any) {
    const token = localStorage.getItem('authToken') || '';

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(
      `${this.API_URL}/api/telemedicine-consultant/get/${id}`,
      { headers },
    );
  }

  addConsultationNotes(id: any, payload: any) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.http.post(
      `${this.API_URL}/api/add-notes/${id}`,
      JSON.stringify(payload),
      { headers },
    );
  }

  getRecentNotifications(id: any) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.API_URL}/api/user/notifications/${id}`, {
      headers,
    });
  }

  updateProfile(formData: FormData): Observable<any> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.put(
      `${this.API_URL}/api/user/update-doctor-profile`,
      formData,
      {
        headers,
      },
    );
  }

  getUserProfileRecord(): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.API_URL}/api/user/doctor-profile`, {
      headers,
    });
  }

  addPrescription(formData: FormData): Observable<any> {
    const token = localStorage.getItem('authToken');
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.http.post(`${this.API_URL}/api/prescription`, formData, {
      headers,
    });
  }

  createBookingLogs(payload: any) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.API_URL}/api/booking-logs`, payload, {
      headers,
    });
  }

  getBookingLogs(userId: any, bookingId: any) {
    const token = localStorage.getItem('authToken') || '';

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(
      `${this.API_URL}/api/booking-logs/${userId}/${bookingId}`,
      { headers },
    );
  }

  bookTelemedicineConsultant(data: any) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.API_URL}/api/add-manual-consultation`, data, {
      headers,
    });
  }

  getAlacartePrice(data: any): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.API_URL}/api/user/currency-price`, data, {
      headers,
    });
  }

  getSlots(data: any) {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });
    return this.http.post(`${this.API_URL}/api/user/slots`, data, {
      headers,
    });
  }

  getUserPhone(phone: any) {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.get(`${this.API_URL}/api/auth/get-user/${phone}`, {
      headers,
    });
  }

  VerifyTentureStatus(data: any): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.API_URL}/api/user/verify-plan-tenure`, data, {
      headers,
    });
  }

  getProgessConsultation() {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.API_URL}/api/get-inprogress-consultation`, {
      headers,
    });
  }

  getUserUnlockMedicals(userId: any): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(
      `${this.API_URL}/api/medical-record/user-unlock-records/${userId}`,
      {
        headers,
      },
    );
  }

  updateNotification(data: any): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.patch(`${this.API_URL}/api/update-notification`, data, {
      headers,
    });
  }

  getNotificationByBookingId(userId: any): Observable<any> {
    const token = localStorage.getItem('authToken') || '';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    return this.http.get(`${this.API_URL}/api/get-notification/${userId}`, {
      headers,
    });
  }

  updateFcmToken(userData: any): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.http.post(
      `${this.API_URL}/api/auth/update-fcm-token`,
      JSON.stringify(userData),
      { headers },
    );
  }
}
